import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject, of } from 'rxjs';
import { tap, filter, map, distinctUntilChanged, switchMap, shareReplay } from 'rxjs/operators';
import { normalizeNodeId } from 'src/app/_helpers/node-id.util';
import { ReadingAchievement } from '../graphs/reading-achievement';
import { environment } from 'src/environments/environment';
import { ProgressReport } from '../graphs/progress-report';
import { PostBaselineProgress } from '../graphs/post-baseline-progress';
import { AssessmentProgress } from '../graphs/assessmentprogress';
import { LoaderService } from '../loader/loader.service';
import { OrgService } from '../_services/org.service';
import { Subscription } from 'rxjs';
@Injectable({
	providedIn: 'root',
})
export class ReportService {
	subscriptions: Subscription[] = [];
	private _readAchieveGraph$: Subject<ReadingAchievement>;
	private _progressReport$: Subject<ProgressReport>;
	private _progAfterBaselineSchool$: Subject<PostBaselineProgress>;
	private _progAfterBaselineClassroom$: Subject<PostBaselineProgress>;
	private _progAssessment$: Subject<AssessmentProgress>;

	// Cache for reading achievement phases data (10 minutes)
	private readingAchievementCache: Map<string, { data: any; timestamp: number }> = new Map();
	private readonly CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes in milliseconds
	private readonly CACHE_STORAGE_KEY = 'readingAchievementCache';
	private defaultTodayFormatted: string | null = new Date().toLocaleString();

	/** One in-flight/completed getProgressReport per node; pruned when classroom changes (see constructor pipe). */
	private progressReportHttpByNodeId = new Map<number, Observable<any>>();

	constructor(private orgService: OrgService, private httpClient: HttpClient, private LoaderService: LoaderService) {
		this._readAchieveGraph$ = new Subject();
		// Replay last report so components recreated after navigation (e.g. back to dashboard)
		// still receive data without a new classroom emission / HTTP round-trip.
		this._progressReport$ = new ReplaySubject<ProgressReport>(1);
		this._progAfterBaselineSchool$ = new Subject();
		this._progAfterBaselineClassroom$ = new Subject();
		this._progAssessment$ = new Subject();

		// Load cache from localStorage on initialization
		this.loadCacheFromStorage();

		this.subscriptions.push(
			this.orgService.selectedClassroom$
				.pipe(
					filter((data: any) => !!data && data.id != null && data.id !== ''),
					map((data: any) => ({ data, nid: normalizeNodeId(data.id) })),
					filter((x): x is { data: any; nid: number } => x.nid != null),
					tap(({ nid }) => {
						for (const k of [...this.progressReportHttpByNodeId.keys()]) {
							if (k !== nid) {
								this.progressReportHttpByNodeId.delete(k);
							}
						}
					}),
					// Same classroom can be re-emitted with a new object (e.g. JSON.parse); compare normalized ids.
					distinctUntilChanged((a, b) => a.nid === b.nid),
					switchMap(({ data }) => this.getProgressReport(data.id))
				)
				.subscribe((data) => {
					console.log('GOT PROGRESS REPORT DATA: ', data);
					this.setProgressReport(data);
				})
		);
	}

	/**
	 * Load cache from localStorage
	 */
	private loadCacheFromStorage(): void {
		try {
			const stored = localStorage.getItem(this.CACHE_STORAGE_KEY);
			if (stored) {
				const parsedCache = JSON.parse(stored);
				const now = Date.now();

				// Filter out expired entries and restore valid ones
				Object.keys(parsedCache).forEach((key) => {
					const entry = parsedCache[key];
					if (entry && entry.timestamp && now - entry.timestamp < this.CACHE_DURATION_MS) {
						this.readingAchievementCache.set(key, {
							data: entry.data,
							timestamp: entry.timestamp,
						});
					}
				});
			}
		} catch (error) {
			console.warn('Failed to load cache from localStorage:', error);
			// If there's an error, start with empty cache
		}
	}

	/**
	 * Save cache to localStorage
	 */
	private saveCacheToStorage(): void {
		try {
			const cacheObject: any = {};
			this.readingAchievementCache.forEach((value, key) => {
				cacheObject[key] = value;
			});
			localStorage.setItem(this.CACHE_STORAGE_KEY, JSON.stringify(cacheObject));
		} catch (error) {
			console.warn('Failed to save cache to localStorage:', error);
			// If localStorage is full or unavailable, continue without persistence
		}
	}

	public getReadingAchievementPhases(nodeId): Observable<any> {
		const cacheKey = String(nodeId);
		const cached = this.readingAchievementCache.get(cacheKey);
		const now = Date.now();

		// Check if we have valid cached data (within 10 minutes)
		if (cached && now - cached.timestamp < this.CACHE_DURATION_MS) {
			// Return cached data - no need to show loader for cached responses
			return of(cached.data);
		}

		// Cache expired or doesn't exist, fetch new data
		this.LoaderService.setLoadingGrowthChart(true);
		return this.httpClient.get<any>(`${environment.apiUrl}/readingAchievement/AllPhases/nodeId/${nodeId}`).pipe(
			tap((data) => {
				// Store in cache with current timestamp
				this.readingAchievementCache.set(cacheKey, {
					data: data,
					timestamp: now,
				});
				// Persist to localStorage
				this.saveCacheToStorage();
			})
		);
	}

	/**
	 * Get the last pulled timestamp for a specific nodeId
	 * @param nodeId The node ID to check
	 * @returns The timestamp in milliseconds, or null if not cached
	 */
	public getLastPulledTimestamp(nodeId: string | number): number | null {
		const cacheKey = String(nodeId);
		const cached = this.readingAchievementCache.get(cacheKey);
		return cached ? cached.timestamp : null;
	}

	/**
	 * Get formatted last pulled timestamp for display
	 * @param nodeId The node ID to check
	 * @returns Formatted date string, or today's date if not cached
	 */
	public getLastPulledFormatted(nodeId: string | number): string {
		const timestamp = this.getLastPulledTimestamp(nodeId);
		if (timestamp) {
			const date = new Date(timestamp);
			return date.toLocaleString();
		}
		// Default to today if no cached timestamp
		// Cache the default value to prevent change detection issues
		if (!this.defaultTodayFormatted) {
			const today = new Date();
			this.defaultTodayFormatted = today.toLocaleString();
		}
		return this.defaultTodayFormatted;
	}

	/**
	 * Clear the cache for a specific nodeId, or all cache if nodeId is not provided
	 * @param nodeId Optional node ID to clear specific cache entry
	 */
	public clearReadingAchievementCache(nodeId?: string | number): void {
		if (nodeId !== undefined) {
			const cacheKey = String(nodeId);
			this.readingAchievementCache.delete(cacheKey);
		} else {
			// Clear all cache
			this.readingAchievementCache.clear();
		}
		// Update localStorage
		this.saveCacheToStorage();
	}

	/**
	 * Get node from common sources (localStorage fallback)
	 * This is a helper method for clearCacheAndReload functionality
	 * @param node Optional node to use directly
	 * @returns Node object with id property, or null if not found
	 */
	public getNodeForCacheReload(node?: any): { id: string | number; [key: string]: any } | null {
		// If node is provided and has an id, use it
		if (node?.id) {
			return node;
		}

		const storedSchool = localStorage.getItem('selectedSchool');
		if (storedSchool) {
			try {
				const school = JSON.parse(storedSchool);
				if (school?.id) {
					return school;
				}
			} catch (e) {
				console.error('Error parsing stored school:', e);
			}
		}

		return null;
	}

	/**
	 * Clear cache and return node for reload
	 * This is a shared utility method that components can use
	 * @param node Optional node to use directly
	 * @returns Node object with id property, or null if not found
	 */
	public clearCacheAndGetNode(node?: any): { id: string | number; [key: string]: any } | null {
		const nodeToReload = this.getNodeForCacheReload(node);

		if (nodeToReload?.id) {
			// Clear cache for current node
			this.clearReadingAchievementCache(nodeToReload.id);
			return nodeToReload;
		}

		console.warn('clearCacheAndGetNode: No node or school ID available to reload');
		return null;
	}
	//getreadingachievementHistory
	public getReadingAchievementHistory(nodeId): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}/report/TestBalloonHistory/nodeId/${nodeId}`);
	}

	public readingAchievementRaw(nodeId, $post): Observable<any> {
		this.LoaderService.setLoadingGrowthChartRaw(true);
		return this.httpClient.post<any>(`${environment.apiUrl}/report/RawBalloon/nodeId/${nodeId}/`, $post);
	}

	public getProgressReport(nodeId: any): Observable<any> {
		const id = normalizeNodeId(nodeId);
		if (id == null) {
			return of(null);
		}
		let shared = this.progressReportHttpByNodeId.get(id);
		if (!shared) {
			this.LoaderService.setLoadingProgressTable(true);
			shared = this.httpClient
				.get<any>(`${environment.apiUrl}/main/getProgressReport/nodeId/${id}`)
				.pipe(shareReplay({ bufferSize: 1, refCount: false }));
			this.progressReportHttpByNodeId.set(id, shared);
		}
		return shared;
	}

	/**
	 * Drop cached progress report for this classroom and refetch so tables/charts show new scores
	 * (e.g. after PARA LMS slideout submit). {@link getProgressReport} is shareReplay-cached per node.
	 */
	public refreshProgressReport(nodeId: any): void {
		const id = normalizeNodeId(nodeId);
		if (id == null) {
			return;
		}
		this.progressReportHttpByNodeId.delete(id);
		this.getProgressReport(nodeId).subscribe({
			next: (data) => {
				if (data != null) {
					this.setProgressReport(data);
				} else {
					this.LoaderService.setLoadingProgressTable(false);
				}
			},
			error: () => {
				this.LoaderService.setLoadingProgressTable(false);
			},
		});
	}

	public getProgAfterBaseline(nodeId, assessmentBookId, assessmentTemplateId): Observable<any> {
		this.LoaderService.setLoadingProgressBarSchool(true);
		return this.httpClient.get<any>(
			`${environment.apiUrl}/main/getProgressAfterBaseline/nodeId/${nodeId}/assessmentBookId/${assessmentBookId}/assessmentTemplateId/${assessmentTemplateId}`
		);
		//return this.httpClient.get<any>(`${environment.apiUrl}/main/getProgressAfterBaseline/`);
	}

	public getProgAfterBaselineForClassroom(nodeId, assessmentBookId, assessmentTemplateId): Observable<any> {
		this.LoaderService.setLoadingProgressBarClassRoom(true);
		return this.httpClient.get<any>(
			`${environment.apiUrl}/main/getProgressAfterBaselineClass/nodeId/${nodeId}/assessmentBookId/${assessmentBookId}/assessmentTemplateId/${assessmentTemplateId}`
		);
		//return this.httpClient.get<any>(`${environment.apiUrl}/main/getProgressAfterBaseline/`);
	}

	public getProgAtBaseline(nodeId, assessmentBookId, assessmentTemplateId): Observable<any> {
		return this.httpClient.get<any>(
			`${environment.apiUrl}/main/getProgressForBaselineSchool/nodeId/${nodeId}/assessmentBookId/${assessmentBookId}/assessmentTemplateId/${assessmentTemplateId}`
		);
		//return this.httpClient.get<any>(`${environment.apiUrl}/main/getProgressAfterBaseline/`);
	}

	public getProgAssessed(nodeId): Observable<any> {
		//Used to display what portion of each classroom is assessed at all, given a school nodeId.
		this.LoaderService.setLoadingAssessmentsBar(true);
		return this.httpClient.get<any>(`${environment.apiUrl}/main/getProgressForSchool/nodeId/${nodeId}`);
		//return this.httpClient.get<any>(`${environment.apiUrl}/main/getProgressAfterBaseline/`);
	}

	public setReadingAchievement(readAchiData: ReadingAchievement) {
		this.LoaderService.setLoadingGrowthChart(false);
		this._readAchieveGraph$.next(readAchiData);
	}

	get readingAchievement() {
		return this._readAchieveGraph$.asObservable();
	}

	public setProgressReport(progressData: ProgressReport) {
		this._progressReport$.next(progressData);
		this.LoaderService.setLoadingProgressTable(false);
	}

	get progressReport() {
		return this._progressReport$.asObservable();
	}

	public setgrogressAfterBaselineSchool(progData: PostBaselineProgress) {
		this.LoaderService.setLoadingProgressBarSchool(false);
		this._progAfterBaselineSchool$.next(progData);
	}
	public setgrogressAfterBaselineClassroom(progData: PostBaselineProgress) {
		this.LoaderService.setLoadingProgressBarClassRoom(false);
		this._progAfterBaselineClassroom$.next(progData);
	}

	get postBaselineProgressSchool() {
		return this._progAfterBaselineSchool$.asObservable();
	}
	get postBaselineProgressClassroom() {
		return this._progAfterBaselineClassroom$.asObservable();
	}

	public setAssessmentProgress(progData: AssessmentProgress) {
		this.LoaderService.setLoadingAssessmentsBar(false);
		this._progAssessment$.next(progData);
	}

	get assessmentProgress() {
		return this._progAssessment$.asObservable();
	}
	//Removed get atBaselineProgress in refactor

	downloadPreviousScores(nodeId): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}/main/downloadPreviousScores/nodeId/${nodeId}`);
	}
	public totalPerChallange$(nodeId: number): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}/report/studentsPerChallange/nodeId/${nodeId}`);
	}
}
